/* DeskeyHUB — Institutional Knowledge Repository (Phase 1)

   Modes:
   - "institution" → Overview: storage per department, totals, billing card, link
     to each department's library. Can open any dept's full manage view.
   - "department"  → Manage: single department's library (folders + docs +
     pending submissions + approve/reject). Uses dept admin's context.

   Uses react-hooks; Deskey theme (green accent, Manrope). */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Library, FolderOpen, FileText, Upload, Plus, Search, X, Trash2, Edit as Pencil,
  ChevronRight, ArrowLeft, ArrowRight, Check, Clock, CheckCircle, XCircle,
  Download, Eye, Users, AlertCircle, Settings, BookOpen, Zap, Shield
} from '../DeskeyIcons';
import { Button } from '../ui/button';
import DocViewer from '../DocViewer';
import { friendlyError } from '../../lib/errors';
import { openFileInNewTab } from '../../lib/authedFile';
import { confirmDialog } from '../ConfirmDialog';
import Loader from '../Loader';

const API = process.env.REACT_APP_BACKEND_URL;

// Subjects mirrored from class creation get a code derived from the name
// ("English" → "ENGLISH"); showing it next to the name reads as a duplicate.
const alnum = (s) => (s || '').replace(/[^a-z0-9]/gi, '').toLowerCase();
const displayCode = (subject) => {
  const code = subject?.code || '';
  return code && !alnum(subject.name).startsWith(alnum(code)) ? code : '';
};

const jsonApi = async (path, opts = {}) => {
  const token = localStorage.getItem('desky_token');
  const res = await fetch(`${API}/api${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...opts.headers,
    },
  });
  // Always read body as text first to avoid double-consumption issues, then parse
  let text = '';
  try { text = await res.text(); } catch { /* swallow */ }
  let data = null;
  if (text) {
    try { data = JSON.parse(text); } catch { /* not JSON */ }
  }
  if (!res.ok) {
    const fallback = res.status === 400 ? 'Invalid two-factor code'
      : res.status === 401 ? 'Session expired — please log in again'
      : res.status === 403 ? 'You don\'t have permission for this action'
      : res.status === 404 ? 'Not found'
      : res.status === 409 ? 'Conflict — that name or code is already in use'
      : res.status === 413 ? 'File is too large'
      : res.status === 415 ? 'Unsupported file type'
      : res.status === 429 ? 'Too many attempts — try again later'
      : `Request failed (${res.status})`;
    const detail = (data && (data.detail || data.message)) || fallback;
    return { detail, _status: res.status, ...(data || {}) };
  }
  return data || {};
};

const formApi = async (path, formData) => {
  const token = localStorage.getItem('desky_token');
  const res = await fetch(`${API}/api${path}`, {
    method: 'POST',
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: formData,
  });
  try { return await res.json(); } catch { return { _error: true, _status: res.status }; }
};

const fmtBytes = (b) => {
  if (!b) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0, n = b;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(n >= 10 || i === 0 ? 0 : 1)} ${u[i]}`;
};
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '--';
const fmtDateTime = (d) => d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true }) : '--';

const fileEmoji = (mime, name) => {
  const ext = (name || '').split('.').pop()?.toLowerCase();
  if ((mime || '').includes('pdf') || ext === 'pdf') return { bg: '#ef4444', label: 'PDF' };
  if (['doc', 'docx'].includes(ext) || (mime || '').includes('word')) return { bg: '#2563eb', label: 'DOC' };
  if (['xls', 'xlsx'].includes(ext)) return { bg: '#059669', label: 'XLS' };
  if (['ppt', 'pptx'].includes(ext)) return { bg: '#ea580c', label: 'PPT' };
  if (['png', 'jpg', 'jpeg', 'webp'].includes(ext)) return { bg: '#8b5cf6', label: 'IMG' };
  if (['txt', 'md', 'csv'].includes(ext)) return { bg: '#64748b', label: ext?.toUpperCase() || 'TXT' };
  return { bg: '#475569', label: 'FILE' };
};

/* ═══════════════════════════════════════════════════════════════════════
   ROOT
═══════════════════════════════════════════════════════════════════════ */
export function DeskeyHubSection({ accent, mode = 'institution' }) {
  // mode: 'institution' | 'department'
  const A = accent || '#34A853';
  const [selectedDept, setSelectedDept] = useState(null); // {id, name} when drilling into a specific repo

  if (mode === 'department') {
    // Single-department mode — skip the overview, go straight to repo manage
    return <DeptManageView A={A} mode={mode} embedded={false} />;
  }

  // Institution overview with drill-down
  if (selectedDept) {
    return (
      <DeptManageView
        A={A}
        mode="institution"
        forcedDept={selectedDept}
        onBack={() => setSelectedDept(null)}
      />
    );
  }
  return <InstitutionOverview A={A} onOpen={(d) => setSelectedDept(d)} />;
}

/* ═══════════════════════════════════════════════════════════════════════
   INSTITUTION — Overview (cards per dept, storage totals, billing)
═══════════════════════════════════════════════════════════════════════ */
function InstitutionOverview({ A, onOpen }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [storageStatus, setStorageStatus] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [d, s] = await Promise.all([
      jsonApi('/hub/institution/overview'),
      jsonApi('/institution/storage/status'),
    ]);
    setData(d);
    if (!s._status) setStorageStatus(s);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) {
    return <Loader />;
  }

  const t = data?.totals || {};

  return (
    <div className="space-y-4" data-testid="hub-institution-overview">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-extrabold text-gray-900 tracking-tight flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.02em' }}>
            <Library className="w-5 h-5" style={{ color: A }} /> DeskeyHUB
          </h2>
          <p className="text-[12px] text-gray-500 mt-0.5">Institutional knowledge repository — every department's library, contributions & storage in one place.</p>
        </div>
      </div>

      {/* Totals strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="hub-totals">
        {[
          { label: 'Departments', val: t.department_count || 0, icon: FolderOpen, color: A },
          { label: 'Documents', val: t.document_count || 0, icon: FileText, color: '#3b82f6' },
          { label: 'Teacher Contribs', val: t.teacher_contributions || 0, icon: Users, color: '#8b5cf6' },
          { label: 'Pending Review', val: t.pending_submissions || 0, icon: Clock, color: (t.pending_submissions || 0) > 0 ? '#f59e0b' : '#64748b' },
        ].map(s => (
          <div key={s.label} className="bg-white border border-gray-200 rounded-xl p-3.5 flex items-center gap-3 hover:shadow-md transition-all">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${s.color}15` }}>
              <s.icon className="w-4 h-4" style={{ color: s.color }} />
            </div>
            <div>
              <p className="text-xl font-extrabold text-gray-900 tabular-nums leading-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>{s.val}</p>
              <p className="text-[10px] uppercase tracking-wider text-gray-500">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Storage & Billing card (plan-based) */}
      {storageStatus && (
        <div className="bg-gradient-to-br from-white via-white to-[#34A853]/5 border border-gray-200 rounded-xl p-4" data-testid="hub-billing-card">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-[#0d9488]">Storage Usage & Billing</p>
              <h3 className="text-lg font-extrabold text-gray-900 mt-0.5" style={{ fontFamily: 'Manrope, sans-serif' }}>{(storageStatus.used_gb || 0).toFixed(2)} GB used</h3>
              <p className="text-[11px] text-gray-600 mt-0.5">
                Plan: <span className="font-bold text-gray-900">{storageStatus.plan_name || 'Free (20 GB)'}</span>
                {` · Quota: `}
                <span className="font-bold text-gray-900">{(storageStatus.quota_gb || 20).toLocaleString('en-IN')} GB</span>
                {` · `}
                <span className="font-bold text-gray-900">₹{(storageStatus.monthly_price || 0)}</span>/mo (included in annual invoice)
              </p>
            </div>
          </div>
          <div className="mt-3 h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div className={`h-full rounded-full transition-all ${storageStatus.used_gb > storageStatus.quota_gb ? 'bg-red-500' : ''}`}
                 style={{ width: `${Math.min(100, ((storageStatus.used_gb || 0) / (storageStatus.quota_gb || 1)) * 100)}%`, background: storageStatus.used_gb > storageStatus.quota_gb ? undefined : A }} />
          </div>
          <p className={`text-[10px] mt-1 ${storageStatus.used_gb > storageStatus.quota_gb ? 'text-red-600 font-semibold' : 'text-gray-400'}`}>
            {(storageStatus.used_gb || 0).toFixed(2)} GB of {((storageStatus.quota_gb || 20)).toLocaleString('en-IN')} GB
            {storageStatus.used_gb > storageStatus.quota_gb && ' — over quota'}
          </p>
          {storageStatus.plan_status === 'pending_change' && (
            <div className="mt-2 text-[10px] text-amber-700 bg-amber-50 border border-amber-100 rounded px-2 py-1">
              Switching to {storageStatus.pending_plan_name || 'Free'} on {storageStatus.next_effective_month}-01
            </div>
          )}
        </div>
      )}

      {/* Departments grid */}
      <div className="bg-white border border-gray-200 rounded-xl" data-testid="hub-dept-grid">
        <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2">
          <h3 className="text-sm font-bold text-gray-900">Department Libraries</h3>
          <span className="text-[10px] text-gray-500">·</span>
          <span className="text-[10px] text-gray-500 tabular-nums">{(data?.departments || []).length} libraries</span>
        </div>
        <div className="p-3">
          {(data?.departments || []).length === 0 ? (
            <p className="text-[12px] text-gray-400 text-center py-8">No departments yet.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {(data.departments || []).map(d => (
                <button
                  key={d.department_id}
                  onClick={() => onOpen({ id: d.department_id, name: d.department_name })}
                  className="bg-white border border-gray-200 rounded-xl p-3.5 text-left hover:shadow-md hover:-translate-y-0.5 hover:border-[#34A853]/40 transition-all group"
                  data-testid={`hub-dept-card-${d.department_id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm" style={{ background: `${A}15` }}>
                      <Library className="w-5 h-5" style={{ color: A }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <h4 className="text-sm font-extrabold text-gray-900 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>{d.department_name}</h4>
                        <ArrowRight className="w-3.5 h-3.5 text-gray-300 group-hover:text-[#34A853] transition-transform group-hover:translate-x-0.5 flex-shrink-0" />
                      </div>
                      <p className="text-[11px] text-gray-500 mt-0.5">{d.document_count || 0} documents · {fmtBytes(d.storage_used_bytes)}</p>
                      {d.pending_submissions > 0 && (
                        <span className="inline-flex items-center gap-1 mt-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-100">
                          <Clock className="w-2.5 h-2.5" /> {d.pending_submissions} pending
                        </span>
                      )}
                      {d.teacher_contributions > 0 && (
                        <span className="inline-flex items-center gap-1 mt-1.5 ml-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-purple-50 text-purple-700 border border-purple-100">
                          <Users className="w-2.5 h-2.5" /> {d.teacher_contributions} from teachers
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   DEPARTMENT MANAGE VIEW
   - Folders tree browsing
   - Upload + create folder
   - Pending submissions tab
═══════════════════════════════════════════════════════════════════════ */
function DeptManageView({ A, mode, forcedDept, onBack, embedded }) {
  const [myDept, setMyDept] = useState(forcedDept || null); // {id, name}
  const [repo, setRepo] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState(null); // {id, name, code}
  const [currentFolder, setCurrentFolder] = useState(null); // null=root within subject
  const [folders, setFolders] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [tab, setTab] = useState('subjects'); // subjects | submissions | students | settings
  const [submissions, setSubmissions] = useState([]);
  const [search, setSearch] = useState('');
  const [subjectSearch, setSubjectSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Modals
  const [showUpload, setShowUpload] = useState(false);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [showNewSubject, setShowNewSubject] = useState(false);
  const [editingSubject, setEditingSubject] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [reviewingSub, setReviewingSub] = useState(null);

  // For dept admin mode, need to auto-resolve their department
  useEffect(() => {
    if (myDept || mode === 'institution') return;
    (async () => {
      const me = await jsonApi('/auth/me').catch(() => null);
      const deptId = me?.department_id || (me?.department_ids || [])[0];
      if (deptId) {
        // fetch dept info
        const r = await jsonApi(`/hub/repo/${deptId}`);
        if (r?.repository) {
          setMyDept({ id: deptId, name: r.repository.department_name || 'My Department' });
        } else {
          const all = await jsonApi('/hub/departments');
          const found = (all?.departments || []).find(x => x.id === deptId);
          setMyDept(found ? { id: found.id, name: found.name } : { id: deptId, name: 'My Department' });
        }
      } else {
        setError('Your account is not assigned to any department.');
        setLoading(false);
      }
    })();
  }, [myDept, mode]);

  const loadRepo = useCallback(async () => {
    if (!myDept) return;
    const r = await jsonApi(`/hub/repo/${myDept.id}`);
    setRepo(r?.repository || null);
  }, [myDept]);

  const loadSubjects = useCallback(async () => {
    if (!myDept) return;
    const r = await jsonApi(`/hub/repo/${myDept.id}/subjects`);
    setSubjects(r?.subjects || []);
  }, [myDept]);

  const loadFolders = useCallback(async () => {
    if (!myDept || !selectedSubject) { setFolders([]); setBreadcrumbs([]); return; }
    const params = new URLSearchParams();
    params.set('subject_id', selectedSubject.id);
    if (currentFolder) params.set('parent_id', currentFolder.id);
    const r = await jsonApi(`/hub/repo/${myDept.id}/folders?${params}`);
    setFolders(r?.folders || []);
    setBreadcrumbs(r?.breadcrumbs || []);
  }, [myDept, currentFolder, selectedSubject]);

  const loadDocs = useCallback(async () => {
    if (!myDept || !selectedSubject) { setDocuments([]); return; }
    const params = new URLSearchParams();
    params.set('subject_id', selectedSubject.id);
    if (currentFolder) params.set('folder_id', currentFolder.id);
    if (search.trim()) { params.set('q', search.trim()); params.set('folder_id', '*'); }
    const r = await jsonApi(`/hub/repo/${myDept.id}/documents?${params}`);
    setDocuments(r?.documents || []);
  }, [myDept, currentFolder, search, selectedSubject]);

  const loadSubs = useCallback(async () => {
    if (!myDept) return;
    const r = await jsonApi(`/hub/submissions/${myDept.id}`);
    setSubmissions(r?.submissions || []);
  }, [myDept]);

  const initialLoadRef = useRef(true);
  useEffect(() => {
    if (!myDept) return;
    let cancelled = false;
    if (initialLoadRef.current) setLoading(true);
    Promise.all([loadRepo(), loadSubjects(), loadFolders(), loadDocs(), loadSubs()]).finally(() => {
      if (!cancelled) {
        initialLoadRef.current = false;
        setLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, [myDept, loadRepo, loadSubjects, loadFolders, loadDocs, loadSubs]);

  const pendingCount = submissions.filter(s => s.status === 'pending').length;

  if (error) {
    return (
      <div className="p-8 text-center">
        <AlertCircle className="w-12 h-12 text-amber-400 mx-auto mb-2" />
        <p className="text-sm text-gray-600">{error}</p>
      </div>
    );
  }

  if (!myDept || loading) {
    return <Loader />;
  }

  return (
    <div className="space-y-4" data-testid="hub-dept-manage">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          {onBack && (
            <button onClick={onBack} className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center transition-colors" data-testid="hub-back">
              <ArrowLeft className="w-4 h-4 text-gray-600" />
            </button>
          )}
          <div>
            <h2 className="text-xl font-extrabold text-gray-900 tracking-tight flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.02em' }}>
              <Library className="w-5 h-5" style={{ color: A }} /> {myDept.name} · Library
            </h2>
            <p className="text-[12px] text-gray-500 mt-0.5">
              {repo?.subject_count || subjects.length} subject{(repo?.subject_count || subjects.length) === 1 ? '' : 's'} · {repo?.document_count || 0} documents · {fmtBytes(repo?.storage_used_bytes)}
              {repo?.auto_approval_enabled && (
                <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded border border-emerald-100">
                  <Zap className="w-2.5 h-2.5" /> Auto-approval ON
                </span>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {tab === 'subjects' && !selectedSubject && (
            <Button size="sm" onClick={() => setShowNewSubject(true)} style={{ background: A }} className="text-white gap-1.5 h-8" data-testid="hub-new-subject">
              <Plus className="w-3.5 h-3.5" /> New Subject
            </Button>
          )}
          {tab === 'subjects' && selectedSubject && (
            <>
              <Button size="sm" variant="outline" onClick={() => setShowNewFolder(true)} className="gap-1.5 h-8" data-testid="hub-new-folder">
                <Plus className="w-3.5 h-3.5" /> Folder
              </Button>
              <Button size="sm" onClick={() => setShowUpload(true)} style={{ background: A }} className="text-white gap-1.5 h-8" data-testid="hub-upload">
                <Upload className="w-3.5 h-3.5" /> Upload
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex bg-gray-100 rounded-lg p-0.5 gap-0.5 w-fit flex-wrap">
        {[
          { id: 'subjects', label: 'Subjects', icon: BookOpen },
          { id: 'submissions', label: 'Pending Review', icon: Clock, badge: pendingCount },
          { id: 'students', label: 'Student Access', icon: Users },
          { id: 'settings', label: 'Settings', icon: Settings },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => { setTab(t.id); if (t.id !== 'subjects') { setSelectedSubject(null); setCurrentFolder(null); } }}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-semibold transition-all ${tab === t.id ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-800'}`}
            data-testid={`hub-tab-${t.id}`}
          >
            <t.icon className="w-3.5 h-3.5" /> {t.label}
            {!!t.badge && <span className="text-[9px] font-bold bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full tabular-nums">{t.badge}</span>}
          </button>
        ))}
      </div>

      {/* Subjects level (top) */}
      {tab === 'subjects' && !selectedSubject && (
        <SubjectsLevel
          A={A}
          subjects={subjects}
          search={subjectSearch}
          setSearch={setSubjectSearch}
          onOpen={(s) => setSelectedSubject(s)}
          onEdit={(s) => setEditingSubject(s)}
          onDelete={async (s) => {
            if (!(await confirmDialog({ title: 'Delete subject', message: `Delete subject "${s.name}" and ALL its folders & documents?`, danger: true, confirmLabel: 'Delete' }))) return;
            await jsonApi(`/hub/repo/${myDept.id}/subjects/${s.id}`, { method: 'DELETE' });
            loadSubjects(); loadRepo();
          }}
        />
      )}

      {/* Subject inner browse (folders + docs scoped to selectedSubject) */}
      {tab === 'subjects' && selectedSubject && (
        <>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-1.5 text-[11px] flex-wrap" data-testid="hub-breadcrumbs">
              <button onClick={() => { setSelectedSubject(null); setCurrentFolder(null); setSearch(''); }} className="flex items-center gap-1 px-2 py-1 rounded-md text-gray-600 hover:bg-gray-100 transition-colors" data-testid="hub-crumb-subjects">
                <BookOpen className="w-3 h-3" /> Subjects
              </button>
              <ChevronRight className="w-3 h-3 text-gray-300" />
              <button
                onClick={() => setCurrentFolder(null)}
                className={`flex items-center gap-1 px-2 py-1 rounded-md transition-colors ${!currentFolder ? 'font-bold text-gray-900 bg-gray-100' : 'text-gray-600 hover:bg-gray-100'}`}
                data-testid="hub-crumb-subject"
              >
                {selectedSubject.name}{displayCode(selectedSubject) ? ` (${displayCode(selectedSubject)})` : ''}
              </button>
              {breadcrumbs.map((c, i) => (
                <React.Fragment key={c.id}>
                  <ChevronRight className="w-3 h-3 text-gray-300" />
                  <button onClick={() => setCurrentFolder(c)} className={`px-2 py-1 rounded-md transition-colors ${i === breadcrumbs.length - 1 ? 'font-bold text-gray-900 bg-gray-100' : 'text-gray-600 hover:bg-gray-100'}`}>
                    {c.name}
                  </button>
                </React.Fragment>
              ))}
            </div>
            <div className="relative ml-auto max-w-xs">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search documents, tags..." className="h-8 pl-8 pr-3 text-xs rounded-lg border border-gray-200 focus:outline-none focus:ring-1 focus:ring-gray-300 bg-white w-full" data-testid="hub-search" />
            </div>
          </div>

          {!search.trim() && folders.length > 0 && (
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1.5">Folders</p>
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-2">
                {folders.map(f => (
                  <FolderCard key={f.id} A={A} folder={f} onOpen={() => setCurrentFolder({ id: f.id, name: f.name })} onDelete={async () => {
                    if (!(await confirmDialog({ title: 'Delete folder', message: `Delete folder "${f.name}" and ALL its documents?`, danger: true, confirmLabel: 'Delete' }))) return;
                    await jsonApi(`/hub/repo/${myDept.id}/folders/${f.id}`, { method: 'DELETE' });
                    loadFolders(); loadDocs(); loadRepo();
                  }} />
                ))}
              </div>
            </div>
          )}

          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1.5">Documents {search.trim() && `— search: "${search}"`}</p>
            {documents.length === 0 ? (
              <div className="text-center py-10 border border-dashed border-gray-200 rounded-xl">
                <FileText className="w-10 h-10 text-gray-200 mx-auto mb-2" />
                <p className="text-[12px] text-gray-500 font-medium">No documents {search ? 'match your search' : currentFolder ? 'in this folder' : 'in this subject yet'}</p>
                {!search && <Button size="sm" onClick={() => setShowUpload(true)} className="mt-3 gap-1.5" style={{ background: A }} data-testid="hub-empty-upload"><Upload className="w-3.5 h-3.5 text-white" /> <span className="text-white">Upload first document</span></Button>}
              </div>
            ) : (
              <div className="space-y-2">
                {documents.map(d => (
                  <DocRow key={d.id} A={A} doc={d} onPreview={() => setPreviewDoc(d)} onDelete={async () => {
                    if (!(await confirmDialog({ title: 'Delete document', message: `Delete "${d.title}"?`, danger: true, confirmLabel: 'Delete' }))) return;
                    await jsonApi(`/hub/repo/${myDept.id}/documents/${d.id}`, { method: 'DELETE' });
                    loadDocs(); loadRepo();
                  }} />
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Submissions tab */}
      {tab === 'submissions' && (
        <SubmissionsTab A={A} submissions={submissions} subjects={subjects} onReview={(s) => setReviewingSub(s)} />
      )}

      {/* Student access tab */}
      {tab === 'students' && (
        <StudentAccessTab A={A} />
      )}

      {/* Settings tab */}
      {tab === 'settings' && (
        <SettingsTab A={A} repo={repo} deptId={myDept.id} onChanged={() => { loadRepo(); }} />
      )}

      {/* Modals */}
      {showUpload && (
        <UploadModal
          A={A}
          deptId={myDept.id}
          subjectId={selectedSubject?.id}
          subjectName={selectedSubject?.name}
          folderId={currentFolder?.id}
          folderName={currentFolder?.name}
          onClose={() => setShowUpload(false)}
          onDone={() => { setShowUpload(false); loadDocs(); loadRepo(); loadSubjects(); }}
        />
      )}
      {showNewFolder && (
        <NewFolderModal
          A={A}
          deptId={myDept.id}
          subjectId={selectedSubject?.id}
          parentId={currentFolder?.id}
          parentName={currentFolder?.name}
          onClose={() => setShowNewFolder(false)}
          onDone={() => { setShowNewFolder(false); loadFolders(); loadRepo(); }}
        />
      )}
      {showNewSubject && (
        <SubjectModal
          A={A}
          deptId={myDept.id}
          onClose={() => setShowNewSubject(false)}
          onDone={() => { setShowNewSubject(false); loadSubjects(); loadRepo(); }}
        />
      )}
      {editingSubject && (
        <SubjectModal
          A={A}
          deptId={myDept.id}
          subject={editingSubject}
          onClose={() => setEditingSubject(null)}
          onDone={() => { setEditingSubject(null); loadSubjects(); }}
        />
      )}
      {previewDoc && <DocViewer doc={previewDoc} departmentId={myDept.id} onClose={() => setPreviewDoc(null)} accent={A} />}
      {reviewingSub && (
        <ReviewModal
          A={A}
          submission={reviewingSub}
          deptId={myDept.id}
          subjects={subjects}
          folders={folders}
          onClose={() => setReviewingSub(null)}
          onDone={() => { setReviewingSub(null); loadSubs(); loadDocs(); loadRepo(); loadSubjects(); }}
        />
      )}
    </div>
  );
}

/* ── Subjects level ───────────────────────────────────────────────────── */
function SubjectsLevel({ A, subjects, search, setSearch, onOpen, onEdit, onDelete }) {
  const filtered = subjects.filter(s => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (s.name || '').toLowerCase().includes(q) || (s.code || '').toLowerCase().includes(q);
  });
  return (
    <div className="space-y-3" data-testid="hub-subjects-level">
      <div className="relative max-w-xs">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search subjects (name or code)..." className="w-full h-8 pl-8 pr-3 text-xs rounded-lg border border-gray-200 focus:outline-none focus:ring-1 focus:ring-gray-300 bg-white" data-testid="hub-subject-search" />
      </div>
      {filtered.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-gray-200 rounded-xl">
          <BookOpen className="w-12 h-12 text-gray-200 mx-auto mb-2" />
          <p className="text-[13px] text-gray-500 font-medium">{search ? 'No subjects match your search' : 'No subjects yet'}</p>
          <p className="text-[11px] text-gray-400 mt-1">{search ? '' : 'Create a subject to start organizing the library by topic — Physics, Algebra, Literature, etc.'}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2.5">
          {filtered.map(s => (
            <SubjectCard key={s.id} A={A} subject={s} onOpen={() => onOpen(s)} onEdit={() => onEdit(s)} onDelete={() => onDelete(s)} />
          ))}
        </div>
      )}
    </div>
  );
}

function SubjectCard({ A, subject, onOpen, onEdit, onDelete }) {
  const accent = subject.color || A;
  return (
    <div className="relative bg-white border border-gray-200 rounded-xl p-3.5 hover:shadow-md hover:-translate-y-0.5 hover:border-[#34A853]/40 transition-all group" data-testid={`hub-subject-${subject.id}`}>
      <button onClick={onOpen} className="w-full text-left flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm" style={{ background: `${accent}15` }}>
          <BookOpen className="w-5 h-5" style={{ color: accent }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="text-[14px] font-extrabold text-gray-900 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>{subject.name}</h4>
            {displayCode(subject) && <span className="text-[10px] font-bold tabular-nums bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded">{displayCode(subject)}</span>}
          </div>
          {subject.description && <p className="text-[11px] text-gray-600 mt-0.5 line-clamp-2 leading-relaxed">{subject.description}</p>}
          <div className="flex items-center gap-3 mt-1.5 text-[10px] text-gray-500">
            <span className="flex items-center gap-1"><FileText className="w-2.5 h-2.5" /> {subject.document_count || 0} docs</span>
            <span className="flex items-center gap-1"><FolderOpen className="w-2.5 h-2.5" /> {subject.folder_count || 0} folders</span>
            {subject.pending_submission_count > 0 && (
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded border border-amber-100">
                <Clock className="w-2.5 h-2.5" /> {subject.pending_submission_count} pending
              </span>
            )}
          </div>
          {subject.created_by_name && (
            <p className="text-[9px] text-gray-400 mt-1">Created by {subject.created_by_name}{subject.created_by_role === 'teacher' ? ' (teacher)' : ''}</p>
          )}
        </div>
      </button>
      <div className="absolute top-2 right-2 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
        <button onClick={onEdit} className="w-6 h-6 rounded-md hover:bg-gray-100 text-gray-400 hover:text-gray-700 flex items-center justify-center" title="Edit subject" data-testid={`hub-subject-edit-${subject.id}`}><Pencil className="w-3 h-3" /></button>
        <button onClick={onDelete} className="w-6 h-6 rounded-md hover:bg-red-50 text-gray-400 hover:text-red-500 flex items-center justify-center" title="Delete subject" data-testid={`hub-subject-delete-${subject.id}`}><Trash2 className="w-3 h-3" /></button>
      </div>
    </div>
  );
}

/* ── Subject create/edit modal ────────────────────────────────────────── */
function SubjectModal({ A, deptId, subject, onClose, onDone }) {
  const isEdit = !!subject;
  const [name, setName] = useState(subject?.name || '');
  const [code, setCode] = useState(subject?.code || '');
  const [description, setDescription] = useState(subject?.description || '');
  const [color, setColor] = useState(subject?.color || A);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { setErr('Name is required'); return; }
    setSaving(true); setErr('');
    const body = { name: name.trim(), code: code.trim(), description: description.trim(), color };
    const r = isEdit
      ? await jsonApi(`/hub/repo/${deptId}/subjects/${subject.id}`, { method: 'PUT', body: JSON.stringify(body) })
      : await jsonApi(`/hub/repo/${deptId}/subjects`, { method: 'POST', body: JSON.stringify(body) });
    setSaving(false);
    if (r?.detail) { setErr(friendlyError(r)); return; }
    onDone();
  };

  const palette = ['#34A853', '#3b82f6', '#8b5cf6', '#ef4444', '#f59e0b', '#0d9488', '#ec4899', '#64748b'];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="hub-subject-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <form onSubmit={onSubmit} className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between" style={{ background: `linear-gradient(135deg, ${A}08, transparent)` }}>
          <h3 className="text-sm font-extrabold text-gray-900 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <BookOpen className="w-4 h-4" style={{ color: A }} /> {isEdit ? 'Edit Subject' : 'New Subject'}
          </h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-700 p-1.5 rounded-md hover:bg-gray-100"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Name <span className="text-red-500">*</span></label>
            <input value={name} onChange={e => setName(e.target.value)} className="mt-1.5 w-full h-9 text-sm rounded-lg border border-gray-200 px-3 focus:outline-none focus:ring-1 focus:ring-[#34A853]/40" placeholder="e.g. Physics, Algebra, Literature" data-testid="hub-subject-name" autoFocus />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Subject Code</label>
            <input value={code} onChange={e => setCode(e.target.value)} className="mt-1.5 w-full h-9 text-sm rounded-lg border border-gray-200 px-3 focus:outline-none focus:ring-1 focus:ring-[#34A853]/40 tabular-nums" placeholder="PHY101" data-testid="hub-subject-code" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Description</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} rows={2} className="mt-1.5 w-full text-sm rounded-lg border border-gray-200 px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#34A853]/40 resize-none" placeholder="Optional — what's this subject about?" data-testid="hub-subject-desc" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Color</label>
            <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
              {palette.map(c => (
                <button key={c} type="button" onClick={() => setColor(c)} className={`w-7 h-7 rounded-lg transition-all ${color === c ? 'ring-2 ring-offset-2 ring-gray-400 scale-110' : 'hover:scale-105'}`} style={{ background: c }} data-testid={`hub-subject-color-${c}`} aria-label={`Color ${c}`} />
              ))}
            </div>
          </div>
          {err && <p className="text-[11px] text-red-600 bg-red-50 border border-red-100 rounded px-2 py-1.5">{err}</p>}
        </div>
        <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-end gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button type="submit" size="sm" disabled={saving} style={{ background: A }} className="text-white" data-testid="hub-subject-submit">{saving ? 'Saving...' : (isEdit ? 'Save' : 'Create Subject')}</Button>
        </div>
      </form>
    </div>
  );
}

/* ── Settings tab — auto-approval toggle (2FA gated) ──────────────────── */
function SettingsTab({ A, repo, deptId, onChanged }) {
  const [step, setStep] = useState('idle'); // idle | otp_sent | toggling
  const [otp, setOtp] = useState('');
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const enabled = !!repo?.auto_approval_enabled;

  const requestOtp = async () => {
    const r = await jsonApi(`/hub/repo/${deptId}/auto-approval/request-otp`, { method: 'POST' });
    if (r?.detail) { setErr(friendlyError(r)); return; }
    setErr('');
    setStep('otp_sent');
    setOtp('');
  };

  const submitToggle = async (turnOn) => {
    setSaving(true);
    // Don't pre-clear err on retry — only clear after explicit user typing
    const body = turnOn ? { enabled: true, otp: otp.trim() } : { enabled: false };
    let r;
    try {
      r = await jsonApi(`/hub/repo/${deptId}/auto-approval`, { method: 'PUT', body: JSON.stringify(body) });
    } catch (e) {
      r = { detail: 'Network error — please try again' };
    }
    setSaving(false);
    if (r?.detail) {
      setErr(friendlyError(r));
      // Keep OTP input mounted; clear the wrong code so user can retype
      if (turnOn) setOtp('');
      return;
    }
    setErr('');
    setStep('idle'); setOtp('');
    onChanged?.();
  };

  return (
    <div className="space-y-3 max-w-2xl" data-testid="hub-settings-tab">
      <div className={`rounded-xl border p-4 ${enabled ? 'bg-emerald-50/50 border-emerald-200' : 'bg-white border-gray-200'}`} data-testid="hub-auto-approval-card">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: enabled ? '#10b98115' : '#f59e0b15' }}>
            <Zap className="w-5 h-5" style={{ color: enabled ? '#10b981' : '#f59e0b' }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-sm font-extrabold text-gray-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Auto-approve Teacher Submissions</h3>
              {enabled ? (
                <span className="text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">ON</span>
              ) : (
                <span className="text-[10px] font-bold uppercase tracking-wider bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">OFF</span>
              )}
            </div>
            <p className="text-[12px] text-gray-600 mt-1 leading-relaxed">
              When ON, every teacher submission to this department is published immediately — no review queue. Recommended only when you trust your teaching team and want frictionless contribution.
            </p>
            {enabled && repo?.auto_approval_enabled_by_name && (
              <p className="text-[10.5px] text-emerald-700 mt-1.5 flex items-center gap-1">
                <Shield className="w-3 h-3" /> Enabled by {repo.auto_approval_enabled_by_name}{repo.auto_approval_enabled_at ? ` · ${fmtDateTime(repo.auto_approval_enabled_at)}` : ''}
              </p>
            )}
          </div>
        </div>

        {/* Action area */}
        {!enabled && step === 'idle' && (
          <div className="mt-4 pl-13 flex items-start gap-2">
            <Button size="sm" onClick={requestOtp} style={{ background: A }} className="text-white gap-1.5" data-testid="hub-auto-request-otp">
              <Shield className="w-3.5 h-3.5" /> Enable with 2FA
            </Button>
            <p className="text-[10.5px] text-gray-500 self-center">A 6-digit code will confirm your identity before turning auto-approval on.</p>
          </div>
        )}

        {!enabled && step === 'otp_sent' && (
          <div className="mt-4 space-y-3">
            <div className="bg-white border border-gray-200 rounded-lg p-3">
              <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1.5">Enter the 6-digit code</p>
              <div className="flex items-center gap-2">
                <input
                  value={otp}
                  onChange={e => { setOtp(e.target.value.replace(/\D/g, '').slice(0, 6)); if (err) setErr(''); }}
                  maxLength={6}
                  inputMode="numeric"
                  className={`w-32 h-10 text-base font-bold tracking-[0.4em] tabular-nums rounded-lg border-2 px-3 focus:outline-none text-center ${err ? 'border-red-300 focus:border-red-500 bg-red-50' : 'border-gray-200 focus:border-[#34A853]'}`}
                  placeholder="000000"
                  data-testid="hub-auto-otp-input"
                  autoFocus
                />
                <Button size="sm" type="button" onClick={() => submitToggle(true)} disabled={saving || otp.length !== 6} style={{ background: A }} className="text-white" data-testid="hub-auto-confirm">{saving ? 'Verifying...' : 'Confirm'}</Button>
                <Button size="sm" type="button" variant="ghost" onClick={requestOtp}>Resend</Button>
                <Button size="sm" type="button" variant="ghost" onClick={() => { setStep('idle'); setOtp(''); setErr(''); }}>Cancel</Button>
              </div>
              {err && (
                <div className="mt-2 flex items-start gap-1.5 text-[11px] text-red-700 bg-red-50 border border-red-200 rounded-md px-2.5 py-1.5" data-testid="hub-auto-otp-error">
                  <XCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  <span><strong>{err}</strong> · Please enter the 6-digit code shown above.</span>
                </div>
              )}
              <p className="text-[10px] text-gray-400 mt-1.5">Code expires in 5 minutes · 5 attempts max</p>
            </div>
          </div>
        )}

        {enabled && (
          <div className="mt-4 flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => submitToggle(false)} disabled={saving} className="border-red-200 text-red-700 hover:bg-red-50 gap-1.5" data-testid="hub-auto-disable">
              {saving ? 'Disabling...' : <><XCircle className="w-3.5 h-3.5" /> Disable Auto-approval</>}
            </Button>
            <p className="text-[10.5px] text-gray-500">Disabling does NOT require 2FA — switches submissions back to the review queue immediately.</p>
          </div>
        )}
        {err && step === 'idle' && <p className="text-[11px] text-red-600 mt-2">{err}</p>}
      </div>

      {/* Quick stats */}
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <h4 className="text-[12px] font-bold text-gray-900 mb-2 flex items-center gap-1.5" style={{ fontFamily: 'Manrope, sans-serif' }}><BookOpen className="w-3.5 h-3.5" style={{ color: A }} /> Library at a Glance</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {[
            { label: 'Subjects', val: repo?.subject_count || 0 },
            { label: 'Folders', val: repo?.folder_count || 0 },
            { label: 'Documents', val: repo?.document_count || 0 },
            { label: 'Pending', val: repo?.pending_submission_count || 0 },
          ].map(s => (
            <div key={s.label} className="bg-gray-50 rounded-lg p-2.5">
              <p className="text-lg font-extrabold text-gray-900 tabular-nums" style={{ fontFamily: 'Manrope, sans-serif' }}>{s.val}</p>
              <p className="text-[9px] uppercase tracking-wider text-gray-500">{s.label}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── Folder card ──────────────────────────────────────────────────────── */
function FolderCard({ A, folder, onOpen, onDelete }) {
  return (
    <div className="relative bg-white border border-gray-200 rounded-xl p-3 hover:shadow-sm hover:border-[#34A853]/40 transition-all group" data-testid={`hub-folder-${folder.id}`}>
      <button onClick={onOpen} className="w-full text-left flex items-center gap-2.5">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${A}15` }}>
          <FolderOpen className="w-4.5 h-4.5" style={{ color: A, width: 18, height: 18 }} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-bold text-gray-900 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>{folder.name}</p>
          <p className="text-[10px] text-gray-500">{folder.document_count || 0} docs · {folder.subfolder_count || 0} folders</p>
        </div>
      </button>
      <button onClick={onDelete} className="absolute top-2 right-2 w-6 h-6 rounded-md hover:bg-red-50 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center" title="Delete folder" data-testid={`hub-folder-delete-${folder.id}`}>
        <Trash2 className="w-3 h-3" />
      </button>
    </div>
  );
}

/* ── Document row ─────────────────────────────────────────────────────── */
function DocRow({ A, doc, onPreview, onDelete }) {
  const f = fileEmoji(doc.mime_type, doc.file_name);
  const isContrib = doc.is_teacher_contribution;
  return (
    <div className="flex items-center gap-3 bg-white border border-gray-200 rounded-xl p-3 hover:shadow-sm hover:border-[#34A853]/40 transition-all group" data-testid={`hub-doc-${doc.id}`}>
      <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 text-white text-[10px] font-extrabold" style={{ background: f.bg, fontFamily: 'Manrope, sans-serif' }}>{f.label}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-[13.5px] font-bold text-gray-900 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>{doc.title}</p>
          {isContrib && <span className="text-[9px] font-bold uppercase tracking-wider bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded border border-purple-100">Teacher Contribution</span>}
        </div>
        {doc.description && <p className="text-[11px] text-gray-600 line-clamp-1">{doc.description}</p>}
        <div className="flex items-center gap-2 mt-0.5 text-[10px] text-gray-500 flex-wrap">
          <span>{doc.uploader_name}</span>
          <span>·</span>
          <span>{fmtBytes(doc.file_size)}</span>
          <span>·</span>
          <span>{fmtDate(doc.published_at || doc.created_at)}</span>
          {(doc.tags || []).slice(0, 3).map((t, i) => (
            <span key={i} className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{t}</span>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        <button onClick={onPreview} className="text-[11px] font-semibold text-[#0d9488] hover:text-white hover:bg-[#34A853] border border-[#34A853]/30 hover:border-[#34A853] px-2.5 py-1 rounded-md flex items-center gap-1 transition-all" data-testid={`hub-doc-preview-${doc.id}`}>
          <Eye className="w-3 h-3" /> View
        </button>
        <button onClick={onDelete} className="p-1.5 rounded-md hover:bg-red-50 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity" title="Delete" data-testid={`hub-doc-delete-${doc.id}`}>
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

/* ── Upload modal ─────────────────────────────────────────────────────── */
function UploadModal({ A, deptId, subjectId, subjectName, folderId, folderName, onClose, onDone }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState('');
  const [files, setFiles] = useState([]); // multiple files supported
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const inputRef = useRef(null);

  const addFiles = (list) => {
    if (!list || list.length === 0) return;
    const next = [];
    for (let i = 0; i < list.length; i++) {
      const f = list[i];
      if (!f) continue;
      if (f.size > 25 * 1024 * 1024) { setErr('Each file must be under 25 MB'); continue; }
      next.push(f);
    }
    if (next.length === 0) return;
    setErr('');
    setFiles((prev) => [...prev, ...next]);
    if (next.length === 1 && !title) setTitle(next[0].name.replace(/\.[^/.]+$/, ''));
  };

  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (files.length === 0) { setErr('Pick at least one file'); return; }
    if (files.length === 1 && !title.trim()) { setErr('Title is required for single upload'); return; }
    setSaving(true); setErr('');
    // Upload sequentially to preserve order and handle per-file errors
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      const fd = new FormData();
      fd.append('file', f);
      const t = (files.length === 1 ? title.trim() : f.name.replace(/\.[^/.]+$/, ''));
      fd.append('title', t);
      fd.append('description', description.trim());
      if (subjectId) fd.append('subject_id', subjectId);
      if (folderId) fd.append('folder_id', folderId);
      if (tags.trim()) fd.append('tags', tags.trim());
      const r = await formApi(`/hub/repo/${deptId}/documents`, fd);
      if (r?.detail) { setSaving(false); setErr(friendlyError(r)); return; }
    }
    setSaving(false);
    onDone();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="hub-upload-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <form onSubmit={onSubmit} className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[88vh] flex flex-col overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between" style={{ background: `linear-gradient(135deg, ${A}08, transparent)` }}>
          <div className="flex items-center gap-2">
            <Upload className="w-4 h-4" style={{ color: A }} />
            <h3 className="text-sm font-extrabold text-gray-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Upload Document</h3>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-700 p-1.5 rounded-md hover:bg-gray-100"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {(subjectName || folderName) && (
            <div className="bg-gray-50 rounded-lg px-3 py-2 text-[11px] text-gray-700 space-y-0.5">
              {subjectName && <p>Subject: <span className="font-bold">{subjectName}</span></p>}
              {folderName && <p>Folder: <span className="font-bold">{folderName}</span></p>}
            </div>
          )}
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">File <span className="text-red-500">*</span></label>
            <div
              className="mt-1.5 border-2 border-dashed border-gray-200 rounded-xl p-5 text-center cursor-pointer hover:border-[#34A853]/60 transition-colors"
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
              data-testid="hub-upload-dropzone"
            >
              <input ref={inputRef} type="file" multiple onChange={e => addFiles(e.target.files)} className="hidden" accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.md,.csv,.png,.jpg,.jpeg,.webp" data-testid="hub-upload-file-input" />
              {files.length > 0 ? (
                <div className="space-y-2 text-left">
                  {files.map((f, idx) => (
                    <div key={idx} className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg border border-gray-100 bg-gray-50">
                      <div className="flex items-center gap-2 min-w-0">
                        <FileText className="w-4 h-4" style={{ color: A }} />
                        <div className="min-w-0">
                          <p className="text-[12px] font-bold text-gray-900 truncate">{f.name}</p>
                          <p className="text-[10px] text-gray-500">{fmtBytes(f.size)}</p>
                        </div>
                      </div>
                      <button type="button" onClick={() => removeFile(idx)} className="text-gray-400 hover:text-red-600" aria-label="Remove file">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div>
                  <Upload className="w-7 h-7 text-gray-300 mx-auto mb-1.5" />
                  <p className="text-[12px] text-gray-600 font-medium">Click or drop files here</p>
                  <p className="text-[10px] text-gray-400 mt-0.5">PDF, DOC, XLS, PPT, images — up to 25 MB each</p>
                </div>
              )}
            </div>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Title <span className="text-red-500">*</span></label>
            <input value={title} onChange={e => setTitle(e.target.value)} className="mt-1.5 w-full h-9 text-sm rounded-lg border border-gray-200 px-3 focus:outline-none focus:ring-1 focus:ring-[#34A853]/40 focus:border-[#34A853]/40" placeholder={files.length > 1 ? 'Ignored for multiple files — filenames will be used' : 'e.g. Cell Biology Reference Guide'} data-testid="hub-upload-title" disabled={files.length > 1} />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Description</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} rows={3} className="mt-1.5 w-full text-sm rounded-lg border border-gray-200 px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#34A853]/40 focus:border-[#34A853]/40 resize-none" placeholder="Optional — what's inside this document?" data-testid="hub-upload-desc" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Tags (comma-separated)</label>
            <input value={tags} onChange={e => setTags(e.target.value)} className="mt-1.5 w-full h-9 text-sm rounded-lg border border-gray-200 px-3 focus:outline-none focus:ring-1 focus:ring-[#34A853]/40 focus:border-[#34A853]/40" placeholder="physics, class-11, ncert" data-testid="hub-upload-tags" />
          </div>
          {err && <p className="text-[11px] text-red-600 bg-red-50 border border-red-100 rounded px-2 py-1.5">{err}</p>}
        </div>
        <div className="px-6 py-3 border-t border-gray-100 flex items-center justify-end gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button type="submit" size="sm" disabled={saving || files.length === 0} style={{ background: A }} className="text-white gap-1.5" data-testid="hub-upload-submit">
            {saving ? 'Uploading...' : <><Upload className="w-3.5 h-3.5" /> Upload</>}
          </Button>
        </div>
      </form>
    </div>
  );
}

/* ── New folder modal ─────────────────────────────────────────────────── */
function NewFolderModal({ A, deptId, subjectId, parentId, parentName, onClose, onDone }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { setErr('Name is required'); return; }
    setSaving(true); setErr('');
    const r = await jsonApi(`/hub/repo/${deptId}/folders`, {
      method: 'POST',
      body: JSON.stringify({ name: name.trim(), description: description.trim(), parent_id: parentId || null, subject_id: subjectId || null }),
    });
    setSaving(false);
    if (r?.detail) { setErr(friendlyError(r)); return; }
    onDone();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="hub-folder-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <form onSubmit={onSubmit} className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm">
        <div className="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between" style={{ background: `linear-gradient(135deg, ${A}08, transparent)` }}>
          <h3 className="text-sm font-extrabold text-gray-900 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}><FolderOpen className="w-4 h-4" style={{ color: A }} /> New Folder</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-700 p-1.5 rounded-md hover:bg-gray-100"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3">
          {parentName && <p className="text-[11px] text-gray-500">Inside: <span className="font-semibold text-gray-700">{parentName}</span></p>}
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Name <span className="text-red-500">*</span></label>
            <input value={name} onChange={e => setName(e.target.value)} className="mt-1.5 w-full h-9 text-sm rounded-lg border border-gray-200 px-3 focus:outline-none focus:ring-1 focus:ring-[#34A853]/40" placeholder="e.g. Research Papers" data-testid="hub-folder-name" autoFocus />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Description</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} rows={2} className="mt-1.5 w-full text-sm rounded-lg border border-gray-200 px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#34A853]/40 resize-none" placeholder="Optional" data-testid="hub-folder-desc" />
          </div>
          {err && <p className="text-[11px] text-red-600">{err}</p>}
        </div>
        <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-end gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button type="submit" size="sm" disabled={saving} style={{ background: A }} className="text-white" data-testid="hub-folder-submit">{saving ? 'Creating...' : 'Create'}</Button>
        </div>
      </form>
    </div>
  );
}

/* ── Doc preview modal ────────────────────────────────────────────────── */
/* ── Submissions tab ──────────────────────────────────────────────────── */
function SubmissionsTab({ A, submissions, subjects, onReview }) {
  const [filter, setFilter] = useState('pending');
  const filtered = submissions.filter(s => filter === 'all' || s.status === filter);
  const subjectMap = useMemo(() => {
    const m = {};
    (subjects || []).forEach(s => { m[s.id] = s; });
    return m;
  }, [subjects]);
  return (
    <div className="space-y-3" data-testid="hub-submissions-tab">
      <div className="flex items-center gap-2 flex-wrap">
        {['pending', 'approved', 'rejected', 'changes_requested', 'all'].map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`text-[11px] font-semibold px-2.5 py-1 rounded-full transition-colors ${filter === f ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            data-testid={`hub-sub-filter-${f}`}
          >
            {f.replace('_', ' ')} ({submissions.filter(s => f === 'all' ? true : s.status === f).length})
          </button>
        ))}
      </div>
      {filtered.length === 0 ? (
        <div className="text-center py-10 border border-dashed border-gray-200 rounded-xl">
          <Clock className="w-10 h-10 text-gray-200 mx-auto mb-2" />
          <p className="text-[12px] text-gray-500 font-medium">No submissions in this state</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(s => (
            <SubmissionRow key={s.id} A={A} sub={s} subject={subjectMap[s.target_subject_id]} onReview={onReview} />
          ))}
        </div>
      )}
    </div>
  );
}

function SubmissionRow({ A, sub, subject, onReview }) {
  const f = fileEmoji(sub.mime_type, sub.file_name);
  const statusStyle = {
    pending: 'bg-amber-50 text-amber-700 border-amber-100',
    approved: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    rejected: 'bg-red-50 text-red-700 border-red-100',
    changes_requested: 'bg-blue-50 text-blue-700 border-blue-100',
  }[sub.status] || 'bg-gray-50 text-gray-700 border-gray-100';
  return (
    <div className="flex items-center gap-3 bg-white border border-gray-200 rounded-xl p-3 hover:shadow-sm transition-all" data-testid={`hub-sub-${sub.id}`}>
      <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 text-white text-[10px] font-extrabold" style={{ background: f.bg }}>{f.label}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-[13px] font-bold text-gray-900 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>{sub.title}</p>
          <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${statusStyle}`}>{sub.status.replace('_', ' ')}</span>
          {sub.auto_approved && <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border bg-emerald-50 text-emerald-700 border-emerald-100 flex items-center gap-1"><Zap className="w-2.5 h-2.5" /> auto</span>}
        </div>
        <p className="text-[10px] text-gray-500">by {sub.submitter_name} · {fmtBytes(sub.file_size)} · submitted {fmtDate(sub.submitted_at)}{subject ? ` · → ${subject.name}${displayCode(subject) ? ` (${displayCode(subject)})` : ''}` : ''}</p>
        {sub.reviewer_notes && <p className="text-[10px] text-gray-500 italic mt-0.5">"{sub.reviewer_notes}"</p>}
      </div>
      {sub.status === 'pending' || sub.status === 'changes_requested' ? (
        <Button size="sm" onClick={() => onReview(sub)} style={{ background: A }} className="text-white h-7 text-[11px]" data-testid={`hub-sub-review-${sub.id}`}>Review</Button>
      ) : (
        <span className="text-[10px] text-gray-400">{sub.reviewer_name ? `by ${sub.reviewer_name}` : ''}</span>
      )}
    </div>
  );
}

/* ── Review modal ─────────────────────────────────────────────────────── */
function ReviewModal({ A, submission, deptId, subjects, folders, onClose, onDone }) {
  const [action, setAction] = useState('approve');
  const [notes, setNotes] = useState('');
  const [subjectId, setSubjectId] = useState(submission.target_subject_id || '');
  const [folderId, setFolderId] = useState(submission.target_folder_id || '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const subFileUrl = `${API}/api/hub/submissions/${deptId}/${submission.id}/download`;

  const viewFile = () => openFileInNewTab(subFileUrl).catch(() => setErr('Could not open file'));

  const downloadFile = async () => {
    const token = localStorage.getItem('desky_token');
    const res = await fetch(`${subFileUrl}?dl=1`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) { setErr('Download failed'); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = submission.file_name || 'document';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true); setErr('');
    const body = { action, reviewer_notes: notes };
    if (action === 'approve') {
      if (subjectId) body.subject_id = subjectId;
      if (folderId) body.folder_id = folderId;
    }
    const r = await jsonApi(`/hub/submissions/${deptId}/${submission.id}/review`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    setSaving(false);
    if (r?.detail) { setErr(friendlyError(r)); return; }
    onDone();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="hub-review-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <form onSubmit={onSubmit} className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between" style={{ background: `linear-gradient(135deg, ${A}08, transparent)` }}>
          <h3 className="text-sm font-extrabold text-gray-900 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}><Clock className="w-4 h-4" style={{ color: A }} /> Review Submission</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-700 p-1.5 rounded-md hover:bg-gray-100"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-[12px] font-bold text-gray-900">{submission.title}</p>
            <p className="text-[10px] text-gray-500 mt-0.5">by {submission.submitter_name} · {submission.file_name} · {fmtBytes(submission.file_size)}</p>
            {submission.description && <p className="text-[11px] text-gray-700 mt-1.5">{submission.description}</p>}
            {(submission.tags || []).length > 0 && (
              <div className="flex items-center gap-1 mt-1.5 flex-wrap">
                {submission.tags.map((t, i) => <span key={i} className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{t}</span>)}
              </div>
            )}
            {/* Read the actual file before deciding — approving blind was the
                only option before these existed. */}
            <div className="flex items-center gap-2 mt-2.5">
              <button type="button" onClick={viewFile} className="text-[11px] font-semibold text-[#0d9488] hover:text-white hover:bg-[#34A853] border border-[#34A853]/30 hover:border-[#34A853] px-2.5 py-1 rounded-md flex items-center gap-1 transition-all" data-testid="hub-sub-view">
                <Eye className="w-3 h-3" /> View
              </button>
              <button type="button" onClick={downloadFile} className="text-[11px] font-semibold text-gray-600 hover:text-gray-900 border border-gray-200 hover:border-gray-300 px-2.5 py-1 rounded-md flex items-center gap-1 transition-all" data-testid="hub-sub-download">
                <Download className="w-3 h-3" /> Download
              </button>
            </div>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Decision</label>
            <div className="grid grid-cols-3 gap-1.5 mt-1.5">
              {[
                { id: 'approve', label: 'Approve', icon: Check, color: '#10b981' },
                { id: 'request_changes', label: 'Changes', icon: AlertCircle, color: '#3b82f6' },
                { id: 'reject', label: 'Reject', icon: XCircle, color: '#ef4444' },
              ].map(o => (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => setAction(o.id)}
                  className={`flex flex-col items-center gap-1 px-2 py-2.5 rounded-lg border-2 transition-all ${action === o.id ? 'border-current shadow' : 'border-gray-200 hover:border-gray-300'}`}
                  style={action === o.id ? { color: o.color, background: `${o.color}08` } : {}}
                  data-testid={`hub-review-action-${o.id}`}
                >
                  <o.icon className="w-4 h-4" />
                  <span className="text-[11px] font-bold">{o.label}</span>
                </button>
              ))}
            </div>
          </div>
          {action === 'approve' && (subjects?.length > 0 || folders.length > 0) && (
            <div className="space-y-2">
              {subjects?.length > 0 && (
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Target Subject</label>
                  <select value={subjectId} onChange={e => setSubjectId(e.target.value)} className="mt-1.5 w-full h-9 text-sm rounded-lg border border-gray-200 px-2 focus:outline-none focus:ring-1 focus:ring-[#34A853]/40" data-testid="hub-review-subject">
                    <option value="">(No subject)</option>
                    {subjects.map(s => <option key={s.id} value={s.id}>{s.name}{displayCode(s) ? ` (${displayCode(s)})` : ''}</option>)}
                  </select>
                </div>
              )}
              {folders.length > 0 && (
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Target Folder</label>
                  <select value={folderId} onChange={e => setFolderId(e.target.value)} className="mt-1.5 w-full h-9 text-sm rounded-lg border border-gray-200 px-2 focus:outline-none focus:ring-1 focus:ring-[#34A853]/40" data-testid="hub-review-folder">
                    <option value="">Root</option>
                    {folders.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                  </select>
                </div>
              )}
            </div>
          )}
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Notes {action !== 'approve' && <span className="text-red-500">*</span>}</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} className="mt-1.5 w-full text-sm rounded-lg border border-gray-200 px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#34A853]/40 resize-none" placeholder={action === 'approve' ? 'Optional — visible to submitter' : 'Explain what needs to change'} data-testid="hub-review-notes" />
          </div>
          {err && <p className="text-[11px] text-red-600">{err}</p>}
        </div>
        <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-end gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button type="submit" size="sm" disabled={saving || (action !== 'approve' && !notes.trim())} style={{ background: A }} className="text-white" data-testid="hub-review-submit">{saving ? 'Saving...' : 'Submit Review'}</Button>
        </div>
      </form>
    </div>
  );
}

/* ── Student access tab ───────────────────────────────────────────────── */
function StudentAccessTab({ A }) {
  const [students, setStudents] = useState([]);
  const [depts, setDepts] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState(null); // student object

  const load = useCallback(async () => {
    setLoading(true);
    const [s, d] = await Promise.all([
      jsonApi('/hub/access/students'),
      jsonApi('/hub/departments'),
    ]);
    setStudents(s?.students || []);
    setDepts(d?.departments || []);
    // also load classes list (tenant-wide) for the picker
    const cls = await jsonApi('/hub/access/classes').catch(() => null);
    setClasses(cls?.classes || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!q.trim()) return students;
    const qq = q.toLowerCase();
    return students.filter(s =>
      (s.full_name || '').toLowerCase().includes(qq) ||
      (s.email || '').toLowerCase().includes(qq)
    );
  }, [students, q]);

  if (loading) return <Loader />;

  return (
    <div className="space-y-3" data-testid="hub-student-access">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search students..." className="w-full h-8 pl-8 pr-3 text-xs rounded-lg border border-gray-200 bg-white focus:outline-none focus:ring-1 focus:ring-gray-300" data-testid="hub-access-search" />
        </div>
        <span className="text-[10px] text-gray-500 ml-auto">{filtered.length} student{filtered.length !== 1 ? 's' : ''}</span>
      </div>
      {filtered.length === 0 ? (
        <div className="text-center py-10 border border-dashed border-gray-200 rounded-xl">
          <Users className="w-10 h-10 text-gray-200 mx-auto mb-2" />
          <p className="text-[12px] text-gray-500 font-medium">No students found</p>
        </div>
      ) : (
        <div className="space-y-1.5">
          {filtered.map(s => {
            const mode = s.access_mode || 'all';
            const label = mode === 'all' ? 'All departments' : mode === 'department' ? `${(s.allowed_department_ids || []).length} depts` : `${(s.allowed_class_ids || []).length} classes`;
            return (
              <div key={s.id} className="flex items-center gap-3 bg-white border border-gray-200 rounded-xl p-2.5 hover:shadow-sm transition-all" data-testid={`hub-access-row-${s.id}`}>
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#34A853] to-[#0d9488] text-white text-[12px] font-bold flex items-center justify-center flex-shrink-0">{(s.full_name || '?').charAt(0).toUpperCase()}</div>
                <div className="flex-1 min-w-0">
                  <p className="text-[12.5px] font-bold text-gray-900 truncate">{s.full_name}</p>
                  <p className="text-[10px] text-gray-500 truncate">{s.email}</p>
                </div>
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${mode === 'all' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : mode === 'department' ? 'bg-blue-50 text-blue-700 border-blue-100' : 'bg-purple-50 text-purple-700 border-purple-100'}`}>
                  {label}
                </span>
                <button onClick={() => setEditing(s)} className="text-[11px] font-semibold text-[#0d9488] hover:text-white hover:bg-[#34A853] border border-[#34A853]/30 hover:border-[#34A853] px-2 py-1 rounded-md flex items-center gap-1 transition-all" data-testid={`hub-access-edit-${s.id}`}>
                  <Settings className="w-3 h-3" /> Scope
                </button>
              </div>
            );
          })}
        </div>
      )}
      {editing && (
        <AccessScopeModal
          A={A}
          student={editing}
          depts={depts}
          classes={classes}
          onClose={() => setEditing(null)}
          onDone={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function AccessScopeModal({ A, student, depts, classes, onClose, onDone }) {
  const [mode, setMode] = useState(student.access_mode || 'all');
  const [deptIds, setDeptIds] = useState(student.allowed_department_ids || []);
  const [classIds, setClassIds] = useState(student.allowed_class_ids || []);
  const [saving, setSaving] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    await jsonApi(`/hub/access/students/${student.id}`, {
      method: 'PUT',
      body: JSON.stringify({
        access_mode: mode,
        allowed_department_ids: mode === 'department' ? deptIds : [],
        allowed_class_ids: mode === 'class_tags' ? classIds : [],
      }),
    });
    setSaving(false);
    onDone();
  };

  const toggle = (list, setList, id) => setList(list.includes(id) ? list.filter(x => x !== id) : [...list, id]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="hub-access-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <form onSubmit={onSubmit} className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[88vh] flex flex-col overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between" style={{ background: `linear-gradient(135deg, ${A}08, transparent)` }}>
          <div>
            <h3 className="text-sm font-extrabold text-gray-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Library Access — {student.full_name}</h3>
            <p className="text-[10px] text-gray-500">{student.email}</p>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-700 p-1.5 rounded-md hover:bg-gray-100"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {[
            { id: 'all', label: 'All departments', desc: 'Can browse every department library' },
            { id: 'department', label: 'Selected departments', desc: 'Only chosen departments are visible' },
            { id: 'class_tags', label: 'By class tags', desc: 'Only departments linked to selected classes' },
          ].map(o => (
            <label key={o.id} className={`flex items-start gap-2 p-3 rounded-lg border-2 cursor-pointer transition-all ${mode === o.id ? 'border-[#34A853] bg-[#34A853]/5' : 'border-gray-200 hover:border-gray-300'}`} data-testid={`hub-access-mode-${o.id}`}>
              <input type="radio" checked={mode === o.id} onChange={() => setMode(o.id)} className="mt-0.5 accent-[#34A853]" />
              <div>
                <p className="text-[12.5px] font-bold text-gray-900">{o.label}</p>
                <p className="text-[10.5px] text-gray-500">{o.desc}</p>
              </div>
            </label>
          ))}
          {mode === 'department' && (
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1.5">Allowed Departments</p>
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {depts.map(d => (
                  <label key={d.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer">
                    <input type="checkbox" checked={deptIds.includes(d.id)} onChange={() => toggle(deptIds, setDeptIds, d.id)} className="accent-[#34A853]" data-testid={`hub-access-dept-${d.id}`} />
                    <span className="text-[12px] text-gray-800">{d.name}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          {mode === 'class_tags' && (
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1.5">Allowed Classes (dept auto-resolves)</p>
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {(classes || []).map(c => (
                  <label key={c.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer">
                    <input type="checkbox" checked={classIds.includes(c.id)} onChange={() => toggle(classIds, setClassIds, c.id)} className="accent-[#34A853]" data-testid={`hub-access-class-${c.id}`} />
                    <span className="text-[12px] text-gray-800">{c.name || c.class_code}</span>
                  </label>
                ))}
                {(classes || []).length === 0 && <p className="text-[11px] text-gray-400 italic">No classes found</p>}
              </div>
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-end gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button type="submit" size="sm" disabled={saving} style={{ background: A }} className="text-white" data-testid="hub-access-save">{saving ? 'Saving...' : 'Save Scope'}</Button>
        </div>
      </form>
    </div>
  );
}

export default DeskeyHubSection;
